import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, CircularProgress, Alert, Button, Grid, Chip, Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../hooks/useAuth';
import { apiService } from '../../services/api';

const statusColor = {
  active: 'success',
  inactive: 'warning',
  sold: 'error',
};

const Field = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
      {label}
    </Typography>
    <Typography variant="body1">{value || '—'}</Typography>
  </Box>
);

const VehicleDetail = () => {
  const { number } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await apiService.getVehicleByNumber(number);
        if (!mounted) return;
        setVehicle(data);
      } catch (e) {
        if (!mounted) return;
        setError(typeof e === 'string' ? e : (e?.message || 'Failed to load vehicle'));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [number]);

  if (!user?.is_admin) {
    return (
      <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
        <Alert severity="warning">Admin access required.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/driver-register')} sx={{ mb: 2 }}>
          Back to list
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!vehicle) return null;

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/driver-register')}>
          Back
        </Button>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Unit {vehicle.number}
        </Typography>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/admin/driver-register/${encodeURIComponent(vehicle.number)}/edit`)}
        >
          Edit
        </Button>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Field label="Unit Number" value={vehicle.number} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field label="License Plate" value={vehicle.license_plate} />
          </Grid>
          <Grid item xs={12}>
            <Field label="VIN" value={vehicle.vin} />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Field label="Make" value={vehicle.make} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Field label="Model" value={vehicle.model} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Field label="Year" value={vehicle.year} />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Assigned Driver
              </Typography>
              <Typography variant="body1">
                {vehicle.assigned_user_username || (
                  <Chip label="Unassigned" size="small" variant="outlined" />
                )}
              </Typography>
              {vehicle.assigned_user_email && (
                <Typography variant="body2" color="text.secondary">
                  {vehicle.assigned_user_email}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Status
              </Typography>
              <Chip
                label={vehicle.status || 'active'}
                color={statusColor[vehicle.status] || 'default'}
                size="small"
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Field label="Branch" value={vehicle.branch} />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default VehicleDetail;
